import { useState } from 'react';
import { Link as LinkRouter, Outlet, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

import { itemPorCaminho, itensVisiveis } from './navegacao.ts';
import { LARGURA_SIDEBAR, LARGURA_SIDEBAR_RETRAIDA } from './tema.ts';
import { useSessao } from '../features/auth/sessao.tsx';
import { DialogoTrocarSenha } from '../features/auth/DialogoTrocarSenha.tsx';
import { Logo } from '../shared/ui/Logo.tsx';

/**
 * Moldura do sistema: barra lateral de navegação, topo com o nome da tela
 * atual e a página no meio.
 *
 * A lateral é permanente a partir de `md` e vira gaveta temporária abaixo
 * disso — num notebook de escritório ela fica sempre visível, e num tablet não
 * come metade da largura útil.
 */
export function Layout() {
  const tema = useTheme();
  const telaLarga = useMediaQuery(tema.breakpoints.up('md'));
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const [retraida, setRetraida] = useState(lerPreferenciaDeBarra);
  const [menuAncora, setMenuAncora] = useState<HTMLElement | null>(null);
  const [trocandoSenha, setTrocandoSenha] = useState(false);
  const { pathname } = useLocation();
  const { usuario, sair } = useSessao();

  const item = itemPorCaminho(pathname);

  // O menu só monta dentro de uma rota protegida, então `usuario` existe; o
  // fallback evita um `!` e mantém o tipo honesto.
  const itens = itensVisiveis(usuario?.papel ?? 'usuario');

  // A barra só retrai na tela larga: na gaveta do celular ela já abre e fecha
  // inteira.
  const encolhida = telaLarga && retraida;
  const largura = encolhida ? LARGURA_SIDEBAR_RETRAIDA : LARGURA_SIDEBAR;

  function alternarBarra() {
    setRetraida((atual) => {
      gravarPreferenciaDeBarra(!atual);
      return !atual;
    });
  }

  const conteudoLateral = (
    <>
      <Toolbar sx={{ gap: 1.5, px: encolhida ? 1 : undefined }}>
        {!encolhida && (
          <>
            <Logo variante="marca" altura={36} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Contábil
              </Typography>
              <Typography variant="caption" color="text.secondary">
                RK Solutions
              </Typography>
            </Box>
          </>
        )}

        {telaLarga && (
          <Tooltip title={encolhida ? 'Expandir o menu' : 'Retrair o menu'}>
            <IconButton
              onClick={alternarBarra}
              aria-label={encolhida ? 'Expandir o menu' : 'Retrair o menu'}
              size="small"
              sx={{ mx: encolhida ? 'auto' : 0 }}
            >
              {encolhida ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>

      <Divider />

      <List component="nav" sx={{ px: 1, py: 1.5 }}>
        {itens.map(({ caminho, rotulo, icone: Icone }) => {
          const ativo = pathname === caminho;

          return (
            <ListItem key={caminho} disablePadding sx={{ mb: 0.5 }}>
              {/* Retraída, o rótulo some da tela — e o nome do item passa a
                  existir só no tooltip e no aria-label. */}
              <Tooltip title={encolhida ? rotulo : ''} placement="right">
                <ListItemButton
                  component={LinkRouter}
                  to={caminho}
                  selected={ativo}
                  onClick={() => setGavetaAberta(false)}
                  aria-label={rotulo}
                  sx={{ borderRadius: 2, justifyContent: encolhida ? 'center' : undefined }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: encolhida ? 0 : 40,
                      color: ativo ? 'primary.main' : undefined,
                    }}
                  >
                    <Icone fontSize="small" />
                  </ListItemIcon>
                  {!encolhida && (
                    <ListItemText
                      primary={rotulo}
                      slotProps={{
                        primary: { sx: { fontWeight: ativo ? 600 : 500, fontSize: '0.9rem' } },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${largura}px)` },
          ml: { md: `${largura}px` },
          transition: (tema) => tema.transitions.create(['width', 'margin-left']),
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          {!telaLarga && (
            <IconButton
              edge="start"
              onClick={() => setGavetaAberta(true)}
              aria-label="Abrir menu de navegação"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography variant="h1" sx={{ flexGrow: 1 }}>
            {item?.rotulo ?? 'Contábil RK Solutions'}
          </Typography>

          {usuario !== null && (
            <>
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {usuario.nome}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {usuario.papel === 'administrador' ? 'Administrador' : 'Usuário'}
                </Typography>
              </Box>

              <Tooltip title="Minha conta">
                <IconButton
                  onClick={(evento) => setMenuAncora(evento.currentTarget)}
                  aria-label="Abrir menu da conta"
                  aria-haspopup="menu"
                  size="small"
                >
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 15 }}>
                    {iniciais(usuario.nome)}
                  </Avatar>
                </IconButton>
              </Tooltip>

              <Menu
                open={menuAncora !== null}
                anchorEl={menuAncora}
                onClose={() => setMenuAncora(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem disabled sx={{ opacity: '1 !important' }}>
                  <Typography variant="caption" color="text.secondary">
                    Conectado como {usuario.usuario}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setMenuAncora(null);
                    setTrocandoSenha(true);
                  }}
                >
                  <ListItemIcon>
                    <LockResetOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Trocar minha senha
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuAncora(null);
                    sair();
                  }}
                >
                  <ListItemIcon>
                    <LogoutOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Sair
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: largura },
          flexShrink: { md: 0 },
          transition: (tema) => tema.transitions.create('width'),
        }}
      >
        <Drawer
          variant={telaLarga ? 'permanent' : 'temporary'}
          open={telaLarga || gavetaAberta}
          onClose={() => setGavetaAberta(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: telaLarga ? largura : LARGURA_SIDEBAR,
              boxSizing: 'border-box',
              borderRight: 1,
              borderColor: 'divider',
              overflowX: 'hidden',
              transition: (tema) => tema.transitions.create('width'),
            },
          }}
        >
          {conteudoLateral}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${largura}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      <DialogoTrocarSenha aberto={trocandoSenha} aoFechar={() => setTrocandoSenha(false)} />
    </Box>
  );
}

/** Duas letras para o avatar: primeira e última palavra do nome. */
const CHAVE_BARRA = 'contabil.barra-retraida';

/**
 * A escolha de retrair acompanha quem usa, não a sessão: num notebook de tela
 * curta a barra fica fechada, e no monitor do escritório, aberta. Em janela
 * anônima, ou com o armazenamento bloqueado, o acesso lança — e a barra só
 * volta ao padrão.
 */
function lerPreferenciaDeBarra(): boolean {
  try {
    return window.localStorage.getItem(CHAVE_BARRA) === '1';
  } catch {
    return false;
  }
}

function gravarPreferenciaDeBarra(retraida: boolean): void {
  try {
    window.localStorage.setItem(CHAVE_BARRA, retraida ? '1' : '0');
  } catch {
    // Preferência de conforto: perdê-la não quebra nada.
  }
}

function iniciais(nome: string): string {
  const palavras = nome.trim().split(/\s+/);
  const primeira = palavras[0]?.[0] ?? '';
  const ultima = palavras.length > 1 ? (palavras[palavras.length - 1]?.[0] ?? '') : '';
  return `${primeira}${ultima}`.toUpperCase();
}
